import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { apiClient } from '../../api'
import { ChatSummarySchema, ChatSchema, ChatMessageSchema } from '../../types/chatTypes'
import useLanguage from '../utility/useLanguage'

const CHAT_DETAILS_KEY = (chatId: string) => ['chats', chatId]

const generateConversationTitle = (message: string, t: any): string => {
  if (!message) return t('common.newChat')

  // Use the first 30 characters of the user message as the title
  return message.substring(0, 30) + (message.length > 30 ? '...' : '')
}

export const useChat = (chatId?: string) => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const CHAT_SUMMARIES_KEY = ['chatSummaries']

  const {
    data: chat,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: chatId ? CHAT_DETAILS_KEY(chatId) : ['chats', 'none'],
    queryFn: async () => {
      if (!chatId) return null
      try {
        const chatData = await apiClient.chatHistory.fetchChatDetails(chatId)

        // Check if the response indicates the chat doesn't exist
        if (!chatData || (typeof chatData === 'object' && 'detail' in chatData)) {
          throw new Error('Chat not found')
        }

        return chatData
      } catch (error: any) {
        console.error('Error fetching chat details:', error)

        // Check if it's a 404 error or chat not found
        const isNotFound =
          error.message?.includes('not found') || error.message?.includes('Chat not found') || error.status === 404

        // Show appropriate error toast
        toast({
          title: isNotFound ? t('errors.conversationNotFound') : t('errors.serverError'),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })

        // Navigate back to home on error
        navigate('/')

        throw error
      }
    },
    enabled: !!chatId,
  })

  // Function to update a chat in the list (e.g., after a new message)
  const updateChatSummary = (message: string) => {
    if (!chatId) return

    queryClient.setQueryData<ChatSummarySchema[]>([CHAT_SUMMARIES_KEY], (oldData = []) => {
      return oldData.map(chat =>
        chat.id === chatId
          ? {
              ...chat,
              lastUpdated: new Date().toISOString(),
            }
          : chat,
      )
    })
  }

  // Function to update a message in the chat (optimistic update)
  const updateChatMessage = (messageId: string, update: any) => {
    if (!chatId) return

    queryClient.setQueryData<ChatSchema | null>(CHAT_DETAILS_KEY(chatId), oldChat => {
      if (!oldChat) return null

      return {
        ...oldChat,
        messages: oldChat.messages.map(msg => (msg.id === messageId ? { ...msg, ...update } : msg)),
      }
    })
  }

  // Function to add a new message to the chat (optimistic update)
  const addChatMessage = (message: ChatMessageSchema) => {
    if (!chatId) return

    queryClient.setQueryData<ChatSchema | null>(CHAT_DETAILS_KEY(chatId), oldChat => {
      if (!oldChat) return null

      // Update the chat with the new message
      const updatedChat = {
        ...oldChat,
        messages: [...oldChat.messages, message],
        lastUpdated: new Date().toISOString(),
      }

      // If this is a user message, update the title if it's the first one
      if (
        message.is_user &&
        oldChat.title === t('common.newChat') &&
        oldChat.messages.filter(m => m.is_user).length === 0
      ) {
        updatedChat.title = generateConversationTitle(message.content, t)
      }

      return updatedChat
    })

    // Also update the chat summary in the list
    if (message.content) {
      updateChatSummary(message.content)
    }
  }

  return {
    chat,
    isLoading,
    error: error ? 'Failed to load chat details' : null,
    refetch,
    updateChatMessage,
    addChatMessage,
    updateChatSummary,
  }
}
